import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tooltip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  GroupWork as GroupWorkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientPaymentService from '../../services/clientPayment.service';
import PaymentService from '../../services/payment.service';
import { useTranslation } from '../../hooks/useTranslation';

const statusColors = {
  Active: 'success',
  suspended: 'warning',
  ended: 'error',
};

/** Renders dots: cancelled (grey), paid (green), pending (red). */
const PaymentDots = ({ total = 6, paidCount = 0, cancelledCount = 0, size = 10, t }) => {
  const cancelled = Math.min(cancelledCount, total);
  const activeSlots = total - cancelled;
  const paid = Math.min(paidCount, activeSlots);
  const pending = activeSlots - paid;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {Array.from({ length: cancelled }).map((_, i) => (
        <Tooltip key={`c-${i}`} title={t ? t('payments.cancelled') : 'Cancelled'}>
          <DotIcon
            sx={{
              fontSize: size,
              color: 'grey.500',
              filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.2))',
            }}
          />
        </Tooltip>
      ))}
      {Array.from({ length: paid }).map((_, i) => (
        <Tooltip key={`g-${i}`} title={cancelled + i + 1}>
          <DotIcon
            sx={{
              fontSize: size,
              color: 'success.main',
              filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.2))',
            }}
          />
        </Tooltip>
      ))}
      {Array.from({ length: pending }).map((_, i) => (
        <Tooltip key={`r-${i}`} title={cancelled + paid + i + 1}>
          <DotIcon
            sx={{
              fontSize: size,
              color: 'error.main',
              opacity: 0.9,
              filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.2))',
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

const PaymentList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clientPayments, setClientPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerClientPaymentId, setRegisterClientPaymentId] = useState(null);
  const [registerForm, setRegisterForm] = useState({
    client_payment_id: null,
    installments_count: 1,
    mora: false,
    efectivo: false,
    cheque: false,
    tarjeta: false,
    attachment: null,
  });
  const [registerSaving, setRegisterSaving] = useState(false);
  const [membershipOptions, setMembershipOptions] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grouped'
  const [membershipTypeFilter, setMembershipTypeFilter] = useState(''); // '' = all
  const [expandedClients, setExpandedClients] = useState(() => new Set()); // client ids that are expanded (grouped view)

  useEffect(() => {
    fetchClientPayments();
  }, []);

  const fetchClientPayments = async () => {
    setLoading(true);
    try {
      const response = await ClientPaymentService.getAll();
      const list = response.data?.data || [];
      setClientPayments(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching client payments:', error);
      showSnackbar(t('payments.errorLoading'), 'error');
      setClientPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredList = clientPayments.filter((cp) => {
    const term = searchTerm.toLowerCase();
    const clientName = cp.client?.name?.toLowerCase() || '';
    const paymentTypeName = cp.payment_type?.name?.toLowerCase() || '';
    const matchesSearch = clientName.includes(term) || paymentTypeName.includes(term);
    const matchesType = !membershipTypeFilter || cp.payment_type?.name === membershipTypeFilter;
    return matchesSearch && matchesType;
  });

  const membershipTypes = React.useMemo(() => {
    const names = [...new Set(clientPayments.map((cp) => cp.payment_type?.name).filter(Boolean))];
    return names.sort((a, b) => String(a).localeCompare(String(b)));
  }, [clientPayments]);

  const groupedByClient = React.useMemo(() => {
    const map = new Map();
    filteredList.forEach((cp) => {
      const key = cp.client?.id ?? cp.client?.name ?? 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          clientId: cp.client?.id,
          clientName: cp.client?.name || '—',
          clientEmail: cp.client?.email,
          items: [],
        });
      }
      map.get(key).items.push(cp);
    });
    return Array.from(map.values());
  }, [filteredList]);

  const toggleClientExpanded = (clientId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const isClientExpanded = (clientId) => {
    if (expandedClients.size === 0) return true;
    return expandedClients.has(clientId);
  };

  const getTotalDots = (cp) => {
    const pt = cp.payment_type;
    const cuotas = pt?.cuotas;
    if (pt?.indefinite || (cuotas != null && cuotas === 0)) {
      const paid = getPaidCount(cp);
      return paid + 1;
    }
    if (cuotas != null && cuotas > 0) return cuotas;
    const start = cp.start ? new Date(cp.start) : null;
    const end = cp.end_date ? new Date(cp.end_date) : null;
    if (start && end) {
      const months = Math.max(1, Math.round((end - start) / (30 * 24 * 60 * 60 * 1000)));
      return Math.min(24, months);
    }
    return 6;
  };

  const getPaidCount = (cp) => {
    const payments = cp.payments;
    if (Array.isArray(payments)) return payments.length;
    return 0;
  };

  const getInstallmentsLabel = (cp) => {
    if (cp.payment_type?.indefinite || cp.payment_type?.cuotas === 0) {
      return t('paymentTypes.indefiniteLabel');
    }
    return `${t('paymentTypes.installments')}: ${getTotalDots(cp)}`;
  };

  const handleOpenRegister = (cp = null) => {
    setRegisterClientPaymentId(cp?.id || null);
    setMembershipOptions(clientPayments.filter((c) => c.status === 'Active'));
    setRegisterForm({
      client_payment_id: cp?.id || null,
      installments_count: 1,
      mora: false,
      efectivo: false,
      cheque: false,
      tarjeta: false,
    });
    setRegisterOpen(true);
  };

  const handleCloseRegister = () => {
    setRegisterOpen(false);
    setRegisterClientPaymentId(null);
    setRegisterForm({
      client_payment_id: null,
      installments_count: 1,
      mora: false,
      efectivo: false,
      cheque: false,
      tarjeta: false,
      attachment: null,
    });
  };

  const getSelectedMembership = () => {
    const id = registerClientPaymentId || registerForm.client_payment_id;
    return clientPayments.find((c) => c.id === id) || null;
  };

  const getCancelledCount = (cp) => {
    if (!cp) return 0;
    const startFrom = cp.start_from_payment != null ? Number(cp.start_from_payment) : 1;
    return Math.max(0, startFrom - 1);
  };

  const getPendingCount = (cp) => {
    if (!cp) return 0;
    if (cp.payment_type?.indefinite || cp.payment_type?.cuotas === 0) {
      return 1;
    }
    const total = getTotalDots(cp);
    const cancelled = getCancelledCount(cp);
    const paid = getPaidCount(cp);
    const activeSlots = total - cancelled;
    return Math.max(0, activeSlots - paid);
  };

  const handleRegisterPayment = async () => {
    const cp = getSelectedMembership();
    const clientPaymentId = registerClientPaymentId || registerForm.client_payment_id;
    if (!clientPaymentId || !cp) {
      showSnackbar(t('payments.selectMembership'), 'error');
      return;
    }
    const montoPerInstallment = parseFloat(cp.payment_type?.monto);
    if (Number.isNaN(montoPerInstallment) || montoPerInstallment <= 0) {
      showSnackbar(t('payments.errorLoading'), 'error');
      return;
    }
    const pending = getPendingCount(cp);
    if (pending <= 0) {
      showSnackbar(t('payments.noPendingInstallments'), 'error');
      return;
    }
    const count = Math.max(1, Math.min(registerForm.installments_count || 1, pending));
    const monto = montoPerInstallment * count;
    if (!registerForm.efectivo && !registerForm.cheque && !registerForm.tarjeta) {
      showSnackbar(t('payments.selectMethod'), 'error');
      return;
    }
    setRegisterSaving(true);
    try {
      await PaymentService.create({
        client_payment_id: clientPaymentId,
        monto,
        mora: !!registerForm.mora,
        efectivo: !!registerForm.efectivo,
        cheque: !!registerForm.cheque,
        tarjeta: !!registerForm.tarjeta,
        attachment: registerForm.attachment || undefined,
      });
      showSnackbar(t('payments.registeredSuccess'));
      handleCloseRegister();
      fetchClientPayments();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        (error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : t('payments.errorRegistering'));
      showSnackbar(msg, 'error');
    } finally {
      setRegisterSaving(false);
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h5" fontWeight={600}>
              {t('payments.title')}
            </Typography>
            <Box display="flex" gap={1}>
              {hasPermission('payments.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenRegister()}
                >
                  {t('payments.registerPayment')}
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('payments.subtitle')}
          </Typography>

          <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v != null && setViewMode(v)}
              size="small"
              aria-label="view mode"
            >
              <ToggleButton value="list" aria-label="list">
                <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" />
                {t('payments.viewList')}
              </ToggleButton>
              <ToggleButton value="grouped" aria-label="grouped by client">
                <GroupWorkIcon sx={{ mr: 0.5 }} fontSize="small" />
                {t('payments.viewGrouped')}
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('payments.filterByMembershipType')}</InputLabel>
              <Select
                value={membershipTypeFilter || '_all'}
                label={t('payments.filterByMembershipType')}
                onChange={(e) => setMembershipTypeFilter(e.target.value === '_all' ? '' : e.target.value)}
              >
                <MenuItem value="_all">{t('payments.allMembershipTypes')}</MenuItem>
                {membershipTypes.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              placeholder={t('payments.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table sx={viewMode === 'grouped' ? { tableLayout: 'fixed', width: '100%' } : undefined}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, width: viewMode === 'grouped' ? 200 : undefined, minWidth: viewMode === 'grouped' ? 200 : undefined }}>
                    {t('payments.client')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('payments.membership')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    {t('payments.paymentsStatus')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.nextPayment')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('payments.byYear')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {t('common.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('payments.noRecordsFound')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : viewMode === 'grouped' ? (
                  groupedByClient.map((group) => {
                    const clientKey = group.clientId ?? group.clientName;
                    const expanded = isClientExpanded(clientKey);
                    return (
                      <React.Fragment key={clientKey}>
                        <TableRow
                          hover
                          sx={{
                            backgroundColor: expanded ? 'action.hover' : 'grey.50',
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          <TableCell colSpan={7} sx={{ py: 1, verticalAlign: 'middle' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => toggleClientExpanded(clientKey)}
                                aria-label={expanded ? 'collapse' : 'expand'}
                              >
                                {expanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                              <Box>
                                <Typography fontWeight={600}>{group.clientName}</Typography>
                                {group.clientEmail && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {group.clientEmail}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                size="small"
                                label={group.items.length === 1 ? t('payments.oneMembership') : t('payments.manyMemberships', { count: group.items.length })}
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none', verticalAlign: 'top' }}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Table size="small" sx={{ mb: 1, tableLayout: 'fixed', width: '100%' }}>
                                <TableBody>
                                  {group.items.map((cp) => {
                                    const totalDots = getTotalDots(cp);
                                    const paidCount = getPaidCount(cp);
                                    const pendingCount = totalDots - paidCount;
                                    return (
                                      <TableRow key={cp.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                        <TableCell sx={{ width: 200, minWidth: 200, maxWidth: 200, paddingLeft: 1, paddingRight: 0 }} />
                                        <TableCell>
                                          <Chip
                                            label={cp.payment_type?.name || '—'}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 500 }}
                                          />
                                          {cp.payment_type && (
                                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                              {getInstallmentsLabel(cp)} · ${cp.payment_type.monto}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <PaymentDots
                                              total={totalDots}
                                              paidCount={paidCount}
                                              cancelledCount={getCancelledCount(cp)}
                                              size={14}
                                              t={t}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                              {cp.payment_type?.indefinite
                                                ? (cp.start
                                                    ? t('payments.paidCountSinceStart', { count: paidCount, date: formatDate(cp.start) })
                                                    : t('payments.paidCountLabel', { count: paidCount }))
                                                : `${paidCount}/${totalDots - getCancelledCount(cp)} ${pendingCount > 0 ? `(${pendingCount} ${t('payments.pending')})` : ''}`}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>{formatDate(cp.next_payment_date)}</TableCell>
                                        <TableCell>
                                          {cp.is_year_based ? t('common.yes') : t('common.no')}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={t(`clientPayments.status${cp.status?.charAt(0).toUpperCase() + cp.status?.slice(1)}`)}
                                            size="small"
                                            color={statusColors[cp.status] || 'default'}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <Tooltip title={t('common.view')}>
                                              <IconButton size="small" onClick={() => navigate(`/memberships/${cp.id}`)}>
                                                <ViewIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            {hasPermission('payments.create') && cp.status === 'Active' && (
                                              <Tooltip title={t('payments.registerPayment')}>
                                                <IconButton size="small" color="primary" onClick={() => handleOpenRegister(cp)}>
                                                  <AddIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  filteredList.map((cp) => {
                    const totalDots = getTotalDots(cp);
                    const paidCount = getPaidCount(cp);
                    const pendingCount = totalDots - paidCount;
                    return (
                      <TableRow key={cp.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell>
                          <Typography fontWeight={500}>{cp.client?.name || '—'}</Typography>
                          {cp.client?.email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {cp.client.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cp.payment_type?.name || '—'}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                          {cp.payment_type && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                              {getInstallmentsLabel(cp)} · ${cp.payment_type.monto}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <PaymentDots
                              total={totalDots}
                              paidCount={paidCount}
                              cancelledCount={getCancelledCount(cp)}
                              size={14}
                              t={t}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {cp.payment_type?.indefinite
                                ? (cp.start
                                    ? t('payments.paidCountSinceStart', { count: paidCount, date: formatDate(cp.start) })
                                    : t('payments.paidCountLabel', { count: paidCount }))
                                : `${paidCount}/${totalDots - getCancelledCount(cp)} ${pendingCount > 0 ? `(${pendingCount} ${t('payments.pending')})` : ''}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(cp.next_payment_date)}</TableCell>
                        <TableCell>
                          {cp.is_year_based ? t('common.yes') : t('common.no')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`clientPayments.status${cp.status?.charAt(0).toUpperCase() + cp.status?.slice(1)}`)}
                            size="small"
                            color={statusColors[cp.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={t('common.view')}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/memberships/${cp.id}`)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {hasPermission('payments.create') && cp.status === 'Active' && (
                              <Tooltip title={t('payments.registerPayment')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenRegister(cp)}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={registerOpen} onClose={handleCloseRegister} maxWidth="sm" fullWidth>
        <DialogTitle>{t('payments.registerPayment')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!registerClientPaymentId && (
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('payments.selectMembership')}</InputLabel>
                <Select
                  value={registerForm.client_payment_id || ''}
                  label={t('payments.selectMembership')}
                  onChange={(e) =>
                    setRegisterForm((prev) => ({
                      ...prev,
                      client_payment_id: e.target.value || null,
                      installments_count: 1,
                    }))
                  }
                >
                  {membershipOptions.map((cp) => (
                    <MenuItem key={cp.id} value={cp.id}>
                      {cp.client?.name} — {cp.payment_type?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(() => {
              const selectedCp = getSelectedMembership();
              const pending = selectedCp ? getPendingCount(selectedCp) : 0;
              const montoPer = selectedCp ? parseFloat(selectedCp.payment_type?.monto) : 0;
              const isIndefinite = selectedCp?.payment_type?.indefinite || selectedCp?.payment_type?.cuotas === 0;
              const maxInstallments = isIndefinite ? 1 : Math.min(pending, 12);
              if (selectedCp && pending <= 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    {t('payments.noPendingInstallments')}
                  </Typography>
                );
              }
              if (selectedCp && maxInstallments > 0) {
                return (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>{t('payments.howManyInstallments')}</InputLabel>
                    <Select
                      value={isIndefinite ? 1 : registerForm.installments_count}
                      label={t('payments.howManyInstallments')}
                      onChange={(e) =>
                        setRegisterForm((prev) => ({
                          ...prev,
                          installments_count: Number(e.target.value) || 1,
                        }))
                      }
                      disabled={isIndefinite}
                    >
                      {Array.from({ length: maxInstallments }, (_, i) => i + 1).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n} {n === 1 ? t('payments.oneInstallment') : t('payments.installmentsLabel')} — $
                          {(montoPer * n).toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                    {isIndefinite && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {t('payments.indefiniteOneAtATime')}
                      </Typography>
                    )}
                  </FormControl>
                );
              }
              return null;
            })()}
            <FormControlLabel
              control={
                <Checkbox
                  checked={registerForm.mora}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, mora: e.target.checked }))}
                />
              }
              label={t('payments.lateFee')}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={registerForm.efectivo}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, efectivo: e.target.checked }))
                    }
                  />
                }
                label={t('payments.cash')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={registerForm.cheque}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, cheque: e.target.checked }))
                    }
                  />
                }
                label={t('payments.cheque')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={registerForm.tarjeta}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, tarjeta: e.target.checked }))
                    }
                  />
                }
                label={t('payments.card')}
              />
            </Box>
            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                component="label"
                size="small"
              >
                {t('payments.attachmentButton')}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    setRegisterForm((prev) => ({ ...prev, attachment: file || null }));
                  }}
                />
              </Button>
              {registerForm.attachment && (
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {registerForm.attachment.name}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                {t('payments.attachmentHelp')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegister}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleRegisterPayment}
            disabled={
              registerSaving ||
              !(registerClientPaymentId || registerForm.client_payment_id) ||
              getPendingCount(getSelectedMembership()) <= 0
            }
            startIcon={registerSaving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {registerSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentList;
